import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CrudManager from "@/pages/admin/CrudManager";
import { PageTitle, CARD, INPUT, BTN, BTN_GHOST, Table, useList } from "@/pages/admin/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Trash2 } from "lucide-react";

const ROLES = ["super_admin", "group_admin", "brand_manager", "branch_manager", "catering_manager", "food_service_manager", "staff", "finance", "kitchen_ops"];

export function BrandsAdmin() {
  return <CrudManager title="Brands" subtitle="The group supports unlimited brands. Currently operating the primary brand." path="/admin/brands" testid="brands"
    defaults={{ is_active: true, is_primary: false, cuisines: "", social: {} }}
    columns={[{ key: "name", label: "Name" }, { key: "tagline", label: "Tagline" }, { key: "cuisines", label: "Cuisines" }, { key: "is_active", label: "Active", render: (r) => r.is_active ? "Yes" : "No" }]}
    fields={[
      { key: "name", label: "Brand Name", type: "text" },
      { key: "tagline", label: "Tagline", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "story", label: "Brand Story", type: "textarea" },
      { key: "cuisines", label: "Cuisines (comma separated)", type: "tags" },
      { key: "logo_url", label: "Logo URL", type: "text" },
      { key: "hero_image_url", label: "Hero Image URL", type: "text" },
      { key: "phone", label: "Phone", type: "text" },
      { key: "email", label: "Email", type: "text" },
      { key: "zomato_url", label: "Zomato URL", type: "text" },
      { key: "swiggy_url", label: "Swiggy URL", type: "text" },
      { key: "is_active", label: "Active (visible on website)", type: "toggle" },
    ]} />;
}

export function BranchesAdmin() {
  const { data: brands } = useList("/admin/brands", []);
  return <CrudManager title="Branches" subtitle="Manage locations, each with its own ordering URLs and contact." path="/admin/branches" testid="branches"
    defaults={{ is_active: true, brand_id: brands[0]?.id || "" }}
    columns={[{ key: "name", label: "Branch" }, { key: "city", label: "City" }, { key: "phone", label: "Phone" }, { key: "is_active", label: "Active", render: (r) => r.is_active ? "Yes" : "No" }]}
    fields={[
      { key: "name", label: "Branch Name", type: "text" },
      { key: "brand_id", label: "Brand ID", type: "select", options: brands.map((b) => b.id), hint: brands.map((b) => `${b.name} = ${b.id}`).join(" | ") },
      { key: "address", label: "Address", type: "text" },
      { key: "city", label: "City", type: "text" },
      { key: "phone", label: "Phone", type: "text" },
      { key: "zomato_url", label: "Zomato URL", type: "text" },
      { key: "swiggy_url", label: "Swiggy URL", type: "text" },
      { key: "is_active", label: "Active", type: "toggle" },
    ]} />;
}

export function StaffAdmin() {
  return <CrudManager title="Staff" subtitle="Team members, roles and availability across brands and branches." path="/admin/staff" testid="staff"
    defaults={{ is_available: true }}
    columns={[{ key: "name", label: "Name" }, { key: "role", label: "Role" }, { key: "phone", label: "Phone" }, { key: "is_available", label: "Available", render: (r) => r.is_available ? "Yes" : "No" }]}
    fields={[
      { key: "name", label: "Name", type: "text" },
      { key: "role", label: "Role / Designation", type: "text" },
      { key: "phone", label: "Phone", type: "text" },
      { key: "email", label: "Email", type: "text" },
      { key: "responsibilities", label: "Responsibilities", type: "textarea" },
      { key: "is_available", label: "Available", type: "toggle" },
    ]} />;
}

export function CctvAdmin() {
  return <CrudManager title="CCTV Feeds" subtitle="Private kitchen & branch surveillance. Never exposed to the public site." path="/admin/cctv" testid="cctv"
    defaults={{ is_active: true }}
    columns={[{ key: "name", label: "Feed" }, { key: "location", label: "Location" }, { key: "is_active", label: "Active", render: (r) => r.is_active ? "Yes" : "No" }]}
    fields={[
      { key: "name", label: "Feed Name", type: "text" },
      { key: "location", label: "Location", type: "text" },
      { key: "stream_url", label: "Stream URL", type: "text" },
      { key: "is_active", label: "Active", type: "toggle" },
    ]} />;
}

export function PackagesAdmin() {
  return <CrudManager title="Menu Packages" subtitle="Curated wedding & festive pure-veg / Jain packages shown on the public Packages page." path="/admin/packages" testid="packages"
    defaults={{ is_active: true, is_featured: false, price_unit: "per plate", inclusions: "" }}
    columns={[{ key: "title", label: "Package" }, { key: "occasion", label: "Occasion" }, { key: "price", label: "Price", render: (r) => `₹${r.price} ${r.price_unit || ""}` }, { key: "is_active", label: "Active", render: (r) => r.is_active ? "Yes" : "No" }]}
    fields={[
      { key: "title", label: "Package Title", type: "text" },
      { key: "occasion", label: "Occasion", type: "select", options: ["Wedding", "Festive", "Corporate", "Kitty Party", "Private", "Anniversary", "Birthday", "Conference", "Other"] },
      { key: "description", label: "Description", type: "textarea" },
      { key: "price", label: "Price", type: "number" },
      { key: "price_unit", label: "Price Unit", type: "text", hint: "e.g. per plate" },
      { key: "min_guests", label: "Minimum Guests", type: "text" },
      { key: "cuisine", label: "Cuisine", type: "text" },
      { key: "image_url", label: "Image URL", type: "text" },
      { key: "inclusions", label: "Inclusions (comma separated)", type: "tags" },
      { key: "is_featured", label: "Featured", type: "toggle" },
      { key: "is_active", label: "Active (visible on website)", type: "toggle" },
    ]} />;
}

export function ForecastAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/forecast"); setData(r.data); } catch { toast.error("Forecast failed"); }
    finally { setLoading(false); }
  };
  return (
    <div data-testid="admin-forecast">
      <PageTitle title="AI Forecasting" subtitle="AI-assisted demand & staffing insights in plain operational language." right={<button className={BTN} onClick={run} disabled={loading} data-testid="forecast-run"><Sparkles className="h-4 w-4" /> {loading ? "Analyzing…" : "Generate Forecast"}</button>} />
      {!data && !loading && <div className={`${CARD} p-10 text-center text-[#94A3B8]`}>Click "Generate Forecast" to get AI insights based on your live operations.</div>}
      {data && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className={`${CARD} p-5 lg:col-span-2`}>
            <h3 className="mb-3 font-display text-xl text-[#0F172A]">Operational Insight</h3>
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-[#334155]">{data.insight}</div>
          </div>
          <div className={`${CARD} p-5`}>
            <h3 className="mb-3 font-display text-xl text-[#0F172A]">Snapshot</h3>
            <p className="text-sm text-[#64748B]">{data.context}</p>
            <p className="mt-4 text-xs text-[#94A3B8]">Generated {(data.generated_at || "").slice(0, 16).replace("T", " ")}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function UsersAdmin() {
  const { data, loading, reload } = useList("/auth/users", []);
  const [form, setForm] = useState({ role: "staff" });
  const [creating, setCreating] = useState(false);

  const create = async (e) => {
    e.preventDefault(); setCreating(true);
    try { await api.post("/auth/users", form); toast.success("User created"); setForm({ role: "staff" }); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };
  const changeRole = async (u, role) => { await api.put(`/auth/users/${u.user_id}/role`, { role }); toast.success("Role updated"); reload(); };
  const remove = async (u) => { if (!window.confirm("Delete user?")) return; try { await api.delete(`/auth/users/${u.user_id}`); toast.success("Deleted"); reload(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };

  return (
    <div data-testid="admin-users">
      <PageTitle title="Users & Roles" subtitle="Role-based access control for the operations team." />
      <div className={`${CARD} mb-6 p-5`}>
        <h3 className="mb-3 font-display text-lg text-[#0F172A]">Add Operator</h3>
        <form onSubmit={create} className="grid grid-cols-1 gap-3 sm:grid-cols-4" data-testid="user-create-form">
          <input required placeholder="Name" className={INPUT} value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
          <input required type="email" placeholder="Email" className={INPUT} value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
          <input required type="password" placeholder="Password" className={INPUT} value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
          <div className="flex gap-2">
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className={INPUT} data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
            <button className={BTN} disabled={creating} data-testid="user-create-submit">Add</button>
          </div>
        </form>
      </div>
      {loading ? <div className="text-sm text-[#64748B]">Loading…</div> : (
        <Table headers={["Name", "Email", "Provider", "Role", "Actions"]}>
          {data.map((u) => (
            <tr key={u.user_id} className="hover:bg-[#F8F9FA]">
              <td className="px-4 py-3 font-medium text-[#0F172A]">{u.name}</td>
              <td className="px-4 py-3 text-[#334155]">{u.email}</td>
              <td className="px-4 py-3 text-xs text-[#64748B]">{u.auth_provider || "password"}</td>
              <td className="px-4 py-3">
                <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.role === "super_admin"}>
                  <SelectTrigger className={INPUT + " h-8 w-44"}><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="px-4 py-3">{u.role !== "super_admin" && <button className={BTN_GHOST + " !p-2 text-red-600"} onClick={() => remove(u)}><Trash2 className="h-4 w-4" /></button>}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

export function SettingsAdmin() {
  const { data, loading, reload } = useList("/admin/settings", []);
  const s = !Array.isArray(data) ? data : {};
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  useEffect(() => { if (s && s.id) setForm({ ...s, about: s.about || {} }); }, [s?.id, loading]); // eslint-disable-line

  const uploadTo = async (file, field, setBusy, kind) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const full = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, [field]: full }));
      toast.success(`${kind} uploaded — remember to Save`);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const uploadLogo = (file) => uploadTo(file, "logo_url", setUploadingLogo, "Logo");
  const uploadHeroVideo = (file) => uploadTo(file, "hero_video_url", setUploadingVideo, "Video");

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.put("/admin/settings", form); toast.success("Settings saved"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (loading || !form) return <div className="text-sm text-[#64748B]">Loading…</div>;
  return (
    <div data-testid="admin-settings">
      <PageTitle title="Platform Settings" subtitle="Ordering links, notification contact and public About content." />
      <form onSubmit={save} className="max-w-2xl space-y-5" data-testid="settings-form">
        <div className={`${CARD} space-y-4 p-5`}>
          <h3 className="font-display text-lg text-[#0F172A]">Branding</h3>
          <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Logo</label>
            <div className="mt-1 flex items-center gap-4">
              {form.logo_url && <span className="rounded-md bg-white p-2 ring-1 ring-[#E2E8F0]"><img src={form.logo_url} alt="logo" className="h-10 w-auto object-contain" /></span>}
              <label className="cursor-pointer rounded-md border border-dashed border-[#C5A059] px-4 py-2 text-sm font-medium text-[#C5A059] hover:bg-[#C5A059]/5">
                {uploadingLogo ? "Uploading…" : "Upload Logo"}
                <input type="file" accept="image/*" className="hidden" data-testid="settings-logo-upload" onChange={(e) => uploadLogo(e.target.files?.[0])} />
              </label>
            </div>
            <input className={INPUT + " mt-2"} value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="…or paste a logo URL" data-testid="settings-logo-url" /></div>
        </div>
        <div className={`${CARD} space-y-4 p-5`}>
          <h3 className="font-display text-lg text-[#0F172A]">Ordering & Notifications</h3>
          <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Notification Phone</label>
            <input className={INPUT} value={form.notification_phone || ""} onChange={(e) => setForm({ ...form, notification_phone: e.target.value })} data-testid="settings-notify-phone" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Zomato URL</label>
              <input className={INPUT} value={form.zomato_url || ""} onChange={(e) => setForm({ ...form, zomato_url: e.target.value })} data-testid="settings-zomato" /></div>
            <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Swiggy URL</label>
              <input className={INPUT} value={form.swiggy_url || ""} onChange={(e) => setForm({ ...form, swiggy_url: e.target.value })} data-testid="settings-swiggy" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Company Phone</label>
              <input className={INPUT} value={form.company_phone || ""} onChange={(e) => setForm({ ...form, company_phone: e.target.value })} /></div>
            <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Company Email</label>
              <input className={INPUT} value={form.company_email || ""} onChange={(e) => setForm({ ...form, company_email: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">WhatsApp Number (digits only)</label>
              <input className={INPUT} value={form.whatsapp_number || ""} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="919884272560" data-testid="settings-whatsapp" /></div>
            <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Hero Video</label>
              <div className="mt-1 flex items-center gap-3">
                {form.hero_video_url && <video src={form.hero_video_url} muted className="h-12 w-20 rounded object-cover border border-[#E2E8F0]" />}
                <label className="cursor-pointer rounded-md border border-dashed border-[#C5A059] px-4 py-2 text-sm font-medium text-[#C5A059] hover:bg-[#C5A059]/5">
                  {uploadingVideo ? "Uploading…" : "Upload Video"}
                  <input type="file" accept="video/*" className="hidden" data-testid="settings-hero-video-upload" onChange={(e) => uploadHeroVideo(e.target.files?.[0])} />
                </label>
              </div>
              <input className={INPUT + " mt-2"} value={form.hero_video_url || ""} onChange={(e) => setForm({ ...form, hero_video_url: e.target.value })} placeholder="…or paste an mp4 URL (blank = use image)" data-testid="settings-hero-video" /></div>
          </div>
        </div>
        <div className={`${CARD} space-y-4 p-5`}>
          <h3 className="font-display text-lg text-[#0F172A]">About Content (Public)</h3>
          {["story", "philosophy", "vision", "mission"].map((k) => (
            <div key={k}><label className="text-[11px] font-semibold uppercase tracking-wider capitalize text-[#64748B]">{k}</label>
              <textarea rows={2} className={INPUT} value={form.about?.[k] || ""} onChange={(e) => setForm({ ...form, about: { ...form.about, [k]: e.target.value } })} /></div>
          ))}
        </div>
        <button className={BTN} disabled={saving} data-testid="settings-save">{saving ? "Saving…" : "Save Settings"}</button>
      </form>
    </div>
  );
}
